<script lang="ts">
  import { formatRelativeTime } from '@/lib/utils'
  import UserDisplay from '@/components/ui/UserDisplay.svelte'

  interface Post {
    id: number;
    title: string;
    excerpt?: string;
    privacy?: string;
    privacy_level?: string;
    user: {
      name: string;
      nickname?: string;
      organization?: string;
      department?: string;
      team?: string;
      avatar_url?: string;
    };
    categories?: Array<{
      name: string;
      color: string;
    }>;
    views_count: number;
    comments_count: number;
    likes_count: number;
    created_at: string;
  }

  let {
    post,
    href = ''
  }: {
    post: Post;
    href?: string;
  } = $props();
</script>

<a
  {href}
  class="group block card p-5 sm:p-6 card-hoverable"
>
  <!-- Meta info -->
  <div class="flex items-center justify-between mb-3">
    <UserDisplay
      user={post.user}
      privacy={post.privacy_level || post.privacy || 'full_disclosure'}
      size="sm"
      showAvatar={true}
    />
    <span class="text-caption text-content-tertiary shrink-0 ml-3">
      {formatRelativeTime(post.created_at)}
    </span>
  </div>

  <!-- Title & Content -->
  <h3 class="text-title text-content mb-1.5 group-hover:text-primary transition-colors duration-200">
    {post.title}
  </h3>
  {#if post.excerpt}
    <p class="text-body2 text-content-secondary mb-4 line-clamp-2">
      {post.excerpt}
    </p>
  {/if}

  <!-- Stats & Categories -->
  <div class="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-dark-border/40">
    <div class="flex items-center gap-4 text-caption text-content-tertiary">
      <!-- Views -->
      <span class="flex items-center gap-1.5">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
        </svg>
        {post.views_count || 0}
      </span>
      <!-- Comments -->
      <span class="flex items-center gap-1.5">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
        </svg>
        {post.comments_count || 0}
      </span>
      <!-- Likes -->
      <span class="flex items-center gap-1.5">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
        </svg>
        {post.likes_count || 0}
      </span>
    </div>

    {#if post.categories && post.categories.length > 0}
      <div class="flex items-center gap-1.5 overflow-hidden">
        {#each post.categories.slice(0, 2) as category}
          <span
            class="badge shrink-0"
            style="background-color: color-mix(in srgb, {category.color} 12%, transparent);
                   color: {category.color};"
          >
            {category.name}
          </span>
        {/each}
        {#if post.categories.length > 2}
          <span class="badge badge-neutral shrink-0">+{post.categories.length - 2}</span>
        {/if}
      </div>
    {/if}
  </div>
</a>
